import ky, { type KyInstance } from 'ky'
import { createSignal } from 'solid-js'
import { native, type UserInfo } from '@forest/native-services'
import { FailureCauseError } from '@forest/paywall-core'
import { inStep } from './load-failure'
import { shouldMockAPI } from './params'

const mode = import.meta.env.MODE as 'production' | 'staging' | 'dev'

const userInfo = {
  _ready: null as null | Promise<UserInfo>,
  get ready() {
    // Every read waits on this, so its tag names the failure for all of them.
    this._ready ||= inStep('load_user_info', async () => {
      const result = await native.user.getUserInfo()
      if (!result.success) {
        throw new FailureCauseError('native_failed', 'Failed to get user info')
      }
      if (!result.data) {
        throw new FailureCauseError('no_user', 'User info is empty')
      }
      return result.data
    })
    return this._ready
  },
  set ready(value: Promise<UserInfo>) {
    this._ready = value
  },
}

if (import.meta.env.DEV && shouldMockAPI) {
  userInfo.ready = new Promise((r) => {
    setTimeout(() => {
      r({
        user_type: 'legacy',
        user_id: 1,
        server: 'staging',
        token: 'mock_token',
      })
    }, 200)
  })
}

async function refreshToken() {
  const result = await native.user.refreshToken()
  if (!result.success) {
    throw new FailureCauseError(
      'token_refresh_failed',
      'Failed to refresh token',
    )
  }
  userInfo.ready = Promise.resolve(result.data)
}

const base = ky.create({
  hooks: {
    beforeRequest: [
      async (options) => {
        const { user_type: userType, token } = await userInfo.ready
        if (userType === 'legacy') {
          options.headers.set('X-Auth-Token', token)
          options.headers.set('Remember-Token', token)
        } else {
          options.headers.set('Authorization', `Bearer ${token}`)
        }
      },
    ],
    afterResponse: [
      async (request, _, response) => {
        if (response.status === 401 || response.status === 403) {
          await refreshToken()
          const { token } = await userInfo.ready
          request.headers.set('Authorization', `Bearer ${token}`)
          return await ky(request)
        }
        return response
      },
    ],
  },
})

const serviceServerUrls: Record<UserInfo['server'], string> = {
  dev: 'https://forest-api.service.develop-cluster.seekrtech.com',
  staging: 'https://forest-api-staging.seekrtech.com',
  global: 'https://forest-api.seekrtech.com',
  china: 'https://forest-api.upwardsware.com',
}

let _resolvedInfo: UserInfo | null = null
const [_server, _setServer] = createSignal<UserInfo['server']>('global')

userInfo.ready.then((info) => {
  _resolvedInfo = info
  _setServer(info.server)
})

export const getServer = _server

function getServiceUrl(server: UserInfo['server']) {
  if (import.meta.env.DEV) return location.origin + '/service'
  if (mode === 'production') return serviceServerUrls[server]
  if (mode === 'dev') return serviceServerUrls.dev
  return serviceServerUrls.staging
}

export async function getUserServer(): Promise<UserInfo['server']> {
  return (await userInfo.ready).server
}

let _serviceClient: KyInstance | null = null

export async function getServiceClient() {
  if (!_serviceClient) {
    const info = await userInfo.ready
    _resolvedInfo = info
    _setServer(info.server)
    _serviceClient = base.extend({
      prefixUrl: getServiceUrl(info.server),
    })
  }
  return { client: _serviceClient, userId: _resolvedInfo!.user_id }
}
